import React from 'react';
import { css, injectGlobal } from 'emotion';
import Helmet from 'react-helmet';
import Header from '../components/appComponents/Header';
import SectionThree from '../components/SectionThree';
import heroImg from '../assets/img/concierge-hero.jpg';
import busImg from '../assets/img/concierge-bus.jpg';
import { BUSINESS_APP } from '../appLinks';

/* global tw */

injectGlobal(`.row{
  max-width: 82rem;
  @media (webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi){
      max-width: 75rem;
  }
}`);

const styles = {
    page: css`
        ${tw`w-screen relative`};
        background: #0A0A0F;
        background-image:
            radial-gradient(ellipse 70% 50% at 50% 0%, rgba(170,11,202,0.12), transparent 60%),
            radial-gradient(ellipse 40% 30% at 88% 22%, rgba(177,92,255,0.06), transparent 70%);
        font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
        min-height: 100vh;
        padding-bottom: 6rem;
    `,
    hero: css`
        position: relative;
        width: 100%;
        background-image:
            linear-gradient(90deg, rgba(10,10,15,0.94) 0%, rgba(10,10,15,0.74) 38%, rgba(10,10,15,0.32) 70%, rgba(10,10,15,0.55) 100%),
            linear-gradient(180deg, rgba(10,10,15,0.35) 0%, rgba(10,10,15,0) 28%, rgba(10,10,15,0.92) 100%),
            url(${heroImg});
        background-size: cover;
        background-position: center right;
        background-repeat: no-repeat;
    `,
    heroInner: css`
        max-width: 78rem;
        ${tw`mx-auto px-6 lg:px-10`};
        position: relative;
        z-index: 1;
        padding-top: 9rem;
        padding-bottom: 4.5rem;
        min-height: 30rem;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        @media (min-width: 992px) {
            padding-top: 11rem;
            padding-bottom: 6.5rem;
            min-height: 42rem;
        }
    `,
    container: css`
        max-width: 78rem;
        ${tw`mx-auto px-6 lg:px-10 pt-16 lg:pt-24`};
    `,
    eyebrow: css`
        display: inline-flex;
        align-items: center;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.22em;
        font-size: 0.75rem;
        color: #C9A2FF;
        padding: 0.45rem 0.9rem;
        border: 1px solid rgba(201, 162, 255, 0.4);
        border-radius: 9999px;
        background: rgba(201, 162, 255, 0.06);
        margin-bottom: 1.25rem;
    `,
    h1: css`
        font-weight: 800;
        font-size: 2.5rem;
        color: #F5F5F7;
        letter-spacing: -0.035em;
        line-height: 1.1;
        margin: 0 0 1rem 0;
        max-width: 38rem;
        @media (min-width: 992px) { font-size: 3.5rem; }
        span {
            background: linear-gradient(135deg, #FFFFFF 0%, #CB53D1 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
        }
    `,
    lede: css`
        font-size: 1.0625rem;
        color: #BFBFD1;
        line-height: 1.7;
        max-width: 40rem;
        margin: 1rem 0 2.5rem 0;
        @media (min-width: 992px) { font-size: 1.125rem; }
    `,
    heroCtas: css`
        ${tw`flex flex-wrap items-stretch`};
        gap: 0.75rem;
        margin-bottom: 2rem;
    `,
    primaryCta: css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        text-decoration: none;
        font-weight: 700;
        font-size: 1rem;
        letter-spacing: -0.005em;
        padding: 1rem 1.75rem;
        border-radius: 9999px;
        background: linear-gradient(135deg, #AA0BCA 0%, #5500DB 100%);
        box-shadow: 0 8px 22px rgba(170, 11, 202, 0.4), inset 0 1px 0 rgba(255,255,255,0.15);
        transition: all 0.2s ease;
        cursor: pointer;
        border: 0;
        &:hover {
            transform: translateY(-2px);
            filter: brightness(1.15);
            box-shadow: 0 0 24px rgba(170, 11, 202, 0.55), inset 0 1px 0 rgba(255,255,255,0.15);
        }
        &::after {
            content: '→';
            margin-left: 0.55rem;
            font-size: 1.05em;
            transition: transform 0.2s ease;
        }
        &:hover::after { transform: translateX(4px); }
    `,
    secondaryCta: css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #EFE2FF;
        text-decoration: none;
        font-weight: 600;
        font-size: 0.9375rem;
        letter-spacing: -0.005em;
        padding: 1rem 1.5rem;
        border-radius: 9999px;
        background: rgba(201, 162, 255, 0.06);
        border: 1px solid rgba(201, 162, 255, 0.4);
        transition: all 0.2s ease;
        &::before {
            content: '';
            display: inline-block;
            width: 7px;
            height: 7px;
            border-radius: 9999px;
            background: #C9A2FF;
            margin-right: 0.55rem;
            box-shadow: 0 0 0 4px rgba(201,162,255,0.18);
        }
        &:hover {
            background: rgba(201, 162, 255, 0.14);
            border-color: rgba(201, 162, 255, 0.7);
            color: #fff;
        }
    `,
    section: css`
        ${tw`mt-14 lg:mt-20`};
    `,
    busBand: css`
        ${tw`mt-14 lg:mt-20 w-full flex flex-wrap items-center`};
        gap: 2.5rem;
        @media (min-width: 992px) { gap: 4rem; flex-wrap: nowrap; }
        .text {
            flex: 1 1 0;
            min-width: 0;
            .eyebrow {
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.14em;
                font-size: 0.6875rem;
                color: #C9A2FF;
                margin: 0 0 0.875rem 0;
            }
            h3 {
                font-weight: 800;
                font-size: 1.75rem;
                color: #F5F5F7;
                letter-spacing: -0.03em;
                line-height: 1.12;
                margin: 0;
                @media (min-width: 992px) { font-size: 2.25rem; }
            }
            p {
                font-size: 1.0625rem;
                color: #9898A8;
                line-height: 1.7;
                margin: 1rem 0 0 0;
                max-width: 30rem;
            }
        }
        .media {
            position: relative;
            flex: 1 1 0;
            min-width: 0;
            width: 100%;
            height: 17rem;
            border-radius: 18px;
            overflow: hidden;
            border: 1px solid rgba(255, 255, 255, 0.08);
            @media (min-width: 992px) { height: 24rem; }
            img {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
            &::after {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: linear-gradient(115deg, rgba(10,10,15,0.35) 0%, rgba(10,10,15,0) 50%);
            }
        }
    `,
    h2: css`
        font-weight: 800;
        font-size: 1.75rem;
        color: #F5F5F7;
        letter-spacing: -0.025em;
        line-height: 1.15;
        margin: 0 0 1rem 0;
        @media (min-width: 992px) { font-size: 2.25rem; }
        &::after {
            content: '';
            display: block;
            margin-top: 0.875rem;
            height: 2px;
            width: 2.5rem;
            background: #C9A2FF;
            border-radius: 2px;
        }
    `,
    p: css`
        font-size: 1rem;
        color: #9898A8;
        line-height: 1.75;
        margin: 0.5rem 0;
        max-width: 52rem;
    `,
    cardGrid: css`
        ${tw`flex flex-wrap -mx-2 mt-6`};
    `,
    card: css`
        ${tw`mx-2 mb-4 p-5`};
        flex: 1 1 16rem;
        background: rgba(255, 255, 255, 0.025);
        border: 1px solid rgba(255, 255, 255, 0.06);
        border-radius: 14px;
        transition: all 0.25s ease;
        &:hover {
            border-color: rgba(255, 255, 255, 0.12);
            background: rgba(255, 255, 255, 0.04);
            transform: translateY(-2px);
            box-shadow: 0 12px 30px rgba(0, 0, 0, 0.35);
        }
        h5 {
            margin: 0 0 0.5rem 0;
            font-weight: 700;
            font-size: 1rem;
            color: #F5F5F7;
            letter-spacing: -0.01em;
        }
        p {
            margin: 0;
            font-size: 0.9375rem;
            color: #9898A8;
            line-height: 1.65;
        }
    `,
    stepGrid: css`
        ${tw`flex flex-wrap -mx-2 mt-6`};
    `,
    step: css`
        ${tw`mx-2 mb-4 p-5`};
        flex: 1 1 14rem;
        background: rgba(255,255,255,0.02);
        border: 1px solid rgba(255,255,255,0.06);
        border-radius: 14px;
        position: relative;
        .num {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 2rem;
            height: 2rem;
            border-radius: 9999px;
            background: rgba(201, 162, 255, 0.12);
            border: 1px solid rgba(201, 162, 255, 0.45);
            color: #C9A2FF;
            font-weight: 800;
            font-size: 0.875rem;
            letter-spacing: 0.02em;
            margin-bottom: 0.85rem;
        }
        h5 {
            margin: 0 0 0.4rem 0;
            font-weight: 700;
            font-size: 1.0625rem;
            color: #F5F5F7;
            letter-spacing: -0.01em;
        }
        p {
            margin: 0;
            font-size: 0.9375rem;
            color: #9898A8;
            line-height: 1.65;
        }
    `,
    pillRow: css`
        ${tw`flex flex-wrap mt-4`};
        gap: 0.5rem;
        max-width: 60rem;
    `,
    pill: css`
        display: inline-flex;
        align-items: center;
        padding: 0.5rem 0.875rem;
        border-radius: 9999px;
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.10);
        color: #F0ECF9;
        font-size: 0.875rem;
        font-weight: 500;
        letter-spacing: -0.005em;
    `,
    standardsGrid: css`
        ${tw`flex flex-wrap -mx-2 mt-4`};
    `,
    standardCard: css`
        ${tw`mx-2 mb-4 p-5`};
        flex: 1 1 18rem;
        background: rgba(255,255,255,0.02);
        border: 1px solid rgba(255,255,255,0.06);
        border-radius: 14px;
        h5 {
            margin: 0 0 0.5rem 0;
            font-weight: 700;
            font-size: 1.0625rem;
            color: #C9A2FF;
            letter-spacing: -0.01em;
        }
        ul {
            list-style: none;
            padding: 0;
            margin: 0.5rem 0 0;
        }
        li {
            position: relative;
            padding-left: 1.25rem;
            color: #BFBFD1;
            font-size: 0.9375rem;
            line-height: 1.85;
            &::before {
                content: '✓';
                position: absolute;
                left: 0;
                top: 0;
                color: #C9A2FF;
                font-weight: 700;
            }
        }
    `,
    formCard: css`
        ${tw`mt-12 lg:mt-20 p-6 lg:p-10`};
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(170, 11, 202, 0.04) 100%);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 20px;
        max-width: 56rem;
        margin-left: auto;
        margin-right: auto;
        h3 {
            font-weight: 800;
            font-size: 1.5rem;
            color: #F5F5F7;
            letter-spacing: -0.025em;
            margin: 0 0 0.5rem 0;
            @media (min-width: 992px) { font-size: 1.875rem; }
        }
        .form-lede {
            color: #BFBFD1;
            font-size: 1rem;
            line-height: 1.7;
            margin: 0 0 1.75rem 0;
        }
        .field {
            display: flex;
            flex-direction: column;
            margin-bottom: 1rem;
        }
        label {
            font-weight: 600;
            font-size: 0.75rem;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            color: #C9A2FF;
            margin-bottom: 0.4rem;
        }
        input, textarea, select {
            font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
            font-size: 0.9375rem;
            color: #F5F5F7;
            padding: 0.75rem 0.95rem;
            border-radius: 10px;
            background: rgba(10,10,15,0.55);
            border: 1px solid rgba(255,255,255,0.08);
            outline: none;
            transition: all 0.2s ease;
            &:focus {
                border-color: rgba(170, 11, 202, 0.55);
                box-shadow: 0 0 0 4px rgba(170,11,202,0.12);
            }
        }
        textarea { min-height: 6.5rem; resize: vertical; }
        .row {
            display: flex;
            flex-wrap: wrap;
            gap: 1rem;
            .field { flex: 1 1 14rem; }
        }
        .submit-row {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 1rem;
            margin-top: 1.25rem;
        }
        .note {
            color: #9898A8;
            font-size: 0.8125rem;
            line-height: 1.55;
            max-width: 24rem;
        }
    `,
    closing: css`
        ${tw`mt-16 text-center`};
        h3 {
            font-weight: 800;
            font-size: 1.5rem;
            color: #F5F5F7;
            letter-spacing: -0.02em;
            margin: 0 0 0.5rem 0;
        }
        .brand {
            font-weight: 800;
            font-size: 2rem;
            background: linear-gradient(135deg, #FFFFFF 0%, #CB53D1 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin: 1.25rem 0 0.25rem 0;
        }
        .tagline {
            color: #9898A8;
            font-size: 1rem;
            font-style: italic;
        }
    `,
};

const SERVICES = [
    { title: 'Weddings & Events', desc: 'Multi-vehicle coordination for the family and guests, across days and venues.' },
    { title: 'Corporate & Executive', desc: 'Reliable premium travel for leadership, meetings and client movement.' },
    { title: 'Airport Transfers', desc: 'Premium pickups and drops with chauffeur tracking and live flight monitoring.' },
    { title: 'Outstation & Multi-City', desc: 'Stitched itineraries across cities, hotels and stops — handled end to end.' },
];

class Concierge extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            name: '',
            phone: '',
            email: '',
            service: 'Weddings & Events',
            city: '',
            startDate: '',
            details: '',
        };
        this.handleChange = this.handleChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    handleChange(e) {
        this.setState({ [e.target.name]: e.target.value });
    }

    handleSubmit(e) {
        e.preventDefault();
        const { name, phone, email, service, city, startDate, details } = this.state;
        const subject = `Concierge enquiry — ${service}${city ? ' (' + city + ')' : ''}`;
        const body = [
            `Name: ${name}`,
            `Phone: ${phone}`,
            `Email: ${email}`,
            `Service: ${service}`,
            `City: ${city}`,
            `Start date: ${startDate}`,
            '',
            'What you need:',
            details,
        ].join('\n');
        const href = `mailto:hello@zyppys.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        if (typeof window !== 'undefined') {
            window.location.href = href;
        }
    }

    render() {
        return (
            <div className={styles.page}>
                <Helmet
                    title="Concierge Car Service in India — Zyppys"
                    meta={[
                        { name: 'description', content: 'Zyppys Concierge handles premium car service end-to-end — weddings, corporate travel, VIP guests, multi-city itineraries and executive mobility across India.' },
                        { name: 'keywords', content: 'concierge car service, wedding car rental, corporate car service, VIP transportation India, executive car service, multi-city car booking, chauffeur driven luxury cars, premium car rental India' },
                        { name: 'viewport', content: 'width=device-width, initial-scale=1, shrink-to-fit=no' },
                        { property: 'og:title', content: 'Concierge Car Service in India — Zyppys' },
                        { property: 'og:description', content: 'A dedicated team plans the cars, chauffeurs and routes for your weddings, events, corporate travel and VIP guests — across India.' },
                        { property: 'og:type', content: 'website' },
                        { property: 'og:site_name', content: 'Zyppys' },
                    ]}
                    link={[
                        { rel: 'canonical', href: 'https://zyppys.com/concierge/' },
                        { rel: 'icon', type: 'image/png', href: '/favicon.png' },
                    ]}
                />
                <Header iosUrl={BUSINESS_APP.ios} androidUrl={BUSINESS_APP.android} />

                <div className={styles.hero}>
                    <div className={styles.heroInner}>
                        <div className={styles.eyebrow}>&#9733; &nbsp;Concierge Service</div>
                        <h1 className={styles.h1}>A dedicated team for <span>your every journey.</span></h1>
                        <p className={styles.lede}>
                            From a single airport run to a multi-city wedding, one dedicated team plans and runs
                            your entire car operation &mdash; one contact, one quote, end to end.
                        </p>
                        <div className={styles.heroCtas}>
                            <a href="#request" className={styles.primaryCta}>Request a Concierge Callback</a>
                            <a href="tel:+918688901902" className={styles.secondaryCta}>Call us &middot; +91 8688 901 902</a>
                        </div>
                    </div>
                </div>

                <div className={styles.container}>
                    <section className={styles.section}>
                        <h2 className={styles.h2}>What we handle</h2>
                        <p className={styles.p}>
                            Anything that needs a premium car and a human keeping it on track.
                        </p>
                        <div className={styles.cardGrid}>
                            {SERVICES.map(item => (
                                <div key={item.title} className={styles.card}>
                                    <h5>{item.title}</h5>
                                    <p>{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className={styles.busBand}>
                        <div className="text">
                            <p className="eyebrow">Group &amp; guest transport</p>
                            <h3>From the couple&rsquo;s car to the entire guest convoy.</h3>
                            <p>Luxury sedans and SUVs for the VIPs; premium coaches and tempo travellers for the guests &mdash; coordinated as one fleet.</p>
                        </div>
                        <div className="media">
                            <img src={busImg} alt="Luxury coach for wedding guests" />
                        </div>
                    </section>

                </div>

                <SectionThree />

                <div className={styles.container}>
                    <div id="request" className={styles.formCard}>
                        <h3>Request a Concierge Callback</h3>
                        <p className="form-lede">
                            Tell us what you need. A concierge will reach out within business hours, often
                            sooner. For urgent requests, call <a href="tel:+918688901902" style={{color:'#C9A2FF',textDecoration:'none',fontWeight:600}}>+91 8688 901 902</a>.
                        </p>
                        <form onSubmit={this.handleSubmit}>
                            <div className="row">
                                <div className="field">
                                    <label htmlFor="c-name">Your name</label>
                                    <input id="c-name" name="name" type="text" required value={this.state.name} onChange={this.handleChange} />
                                </div>
                                <div className="field">
                                    <label htmlFor="c-phone">Phone</label>
                                    <input id="c-phone" name="phone" type="tel" required value={this.state.phone} onChange={this.handleChange} />
                                </div>
                            </div>
                            <div className="row">
                                <div className="field">
                                    <label htmlFor="c-email">Email</label>
                                    <input id="c-email" name="email" type="email" required value={this.state.email} onChange={this.handleChange} />
                                </div>
                                <div className="field">
                                    <label htmlFor="c-service">Service</label>
                                    <select id="c-service" name="service" value={this.state.service} onChange={this.handleChange}>
                                        {SERVICES.map(s => (<option key={s.title} value={s.title}>{s.title}</option>))}
                                    </select>
                                </div>
                            </div>
                            <div className="field">
                                <label htmlFor="c-details">What do you need?</label>
                                <textarea id="c-details" name="details" placeholder="Number of vehicles, dates, guest count, anything we should know..." value={this.state.details} onChange={this.handleChange} />
                            </div>
                            <div className="submit-row">
                                <button type="submit" className={styles.primaryCta}>Send Request</button>
                                <span className="note">
                                    Opens your email app with the request pre-filled to <strong style={{color:'#C9A2FF'}}>hello@zyppys.com</strong>.
                                </span>
                            </div>
                        </form>
                    </div>

                    <div className={styles.closing}>
                        <h3>Premium mobility, handled.</h3>
                        <div className="brand">Zyppys</div>
                        <div className="tagline">Driven Beautifully.</div>
                    </div>
                </div>
            </div>
        );
    }
}

export default Concierge;
